import { forwardRef, type InputHTMLAttributes, useState } from 'react';
import { cn } from '../lib/utils';

/**
 * Formats a CPF string to the pattern 123.456.789-01
 * @param value - The raw CPF string (digits only or partially formatted)
 * @returns The formatted CPF string
 */
export function formatCpf(value: string): string {
  // Remove all non-digit characters
  const digits = value.replace(/\D/g, '');

  // Limit to 11 digits (valid CPF length)
  const limited = digits.slice(0, 11);

  // Apply CPF formatting: 123.456.789-01
  if (limited.length <= 3) {
    return limited;
  } else if (limited.length <= 6) {
    return `${limited.slice(0, 3)}.${limited.slice(3)}`;
  } else if (limited.length <= 9) {
    return `${limited.slice(0, 3)}.${limited.slice(3, 6)}.${limited.slice(6)}`;
  } else {
    return `${limited.slice(0, 3)}.${limited.slice(3, 6)}.${limited.slice(6, 9)}-${limited.slice(9)}`;
  }
}

/**
 * Strips formatting from a CPF string, returning only digits
 * @param value - The formatted CPF string
 * @returns The raw digits only
 */
export function stripCpfFormatting(value: string): string {
  return value.replace(/\D/g, '');
}

/**
 * Validates a CPF number (basic validation - checks length and pattern)
 * Note: This doesn't validate the check digits, just the format
 * @param cpf - The CPF string (formatted or unformatted)
 * @returns true if the CPF format is valid, false otherwise
 */
export function isValidCpfFormat(cpf: string): boolean {
  const digits = stripCpfFormatting(cpf);
  return digits.length === 11;
}

export interface CpfInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'maxLength'> {
  label?: string;
  error?: string;
  helperText?: string;
  /**
   * Whether to return formatted or unformatted value
   * @default true
   */
  returnFormatted?: boolean;
  /**
   * Callback that receives the CPF value (formatted based on returnFormatted prop)
   */
  onCpfChange?: (value: string, rawValue: string) => void;
}

export const CpfInput = forwardRef<HTMLInputElement, CpfInputProps>(
  (
    {
      className,
      label,
      error,
      helperText,
      placeholder = '123.456.789-01',
      value = '',
      onChange,
      returnFormatted = true,
      onCpfChange,
      id,
      ...props
    },
    ref
  ) => {
    // Internal state for the formatted display value
    const [displayValue, setDisplayValue] = useState(() => formatCpf(typeof value === 'string' ? value : ''));

    // Update display value when the external value changes
    if (typeof value === 'string' && value !== displayValue && formatCpf(value) !== displayValue) {
      setDisplayValue(formatCpf(value));
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;
      const formatted = formatCpf(inputValue);
      const raw = stripCpfFormatting(inputValue);

      setDisplayValue(formatted);

      // Call the onChange handler with the formatted value for the input event
      if (onChange) {
        // Create a synthetic event with the formatted value
        const syntheticEvent = {
          ...e,
          target: {
            ...e.target,
            value: returnFormatted ? formatted : raw,
          },
        } as React.ChangeEvent<HTMLInputElement>;
        onChange(syntheticEvent);
      }

      // Call the onCpfChange callback with both formatted and raw values
      if (onCpfChange) {
        onCpfChange(returnFormatted ? formatted : raw, raw);
      }
    };

    const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-medium text-foreground mb-1"
          >
            {label}
          </label>
        )}
        <div className="relative">
          <input
            ref={ref}
            id={inputId}
            type="text"
            inputMode="numeric"
            maxLength={14} // 123.456.789-01
            value={displayValue}
            onChange={handleChange}
            placeholder={placeholder}
            className={cn(
              'flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm',
              'placeholder:text-muted-foreground',
              'focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent',
              'disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground',
              'transition-colors text-foreground',
              error && 'border-red-500 focus:ring-red-500',
              className
            )}
            {...props}
          />
        </div>
        {error && (
          <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
            <span className="w-3 h-3 flex items-center justify-center">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </span>
            {error}
          </p>
        )}
        {helperText && !error && (
          <p className="mt-1 text-sm text-slate-500">{helperText}</p>
        )}
      </div>
    );
  }
);

CpfInput.displayName = 'CpfInput';
